import React, { useState } from "react";
import SubmitButton from "../generic/SubmitButton";
import InputField from "../generic/InputField";
import AlertBanner from "../generic/AlertBanner";
import Modal from "../generic/Modal";
import { MESSAGE_TYPES, Messages, MessageType } from "../../constants/Messages";

interface EditMaterialModalProps {
    title: string;
    currentValue: string;
    onClose: () => void;
    onSubmit: (newValue: string) => Promise<any>; // Update the return type to capture response data
}

const EditMaterialModal: React.FC<EditMaterialModalProps> = ({ title, currentValue, onClose, onSubmit }) => {
    const [ value, setValue ] = useState<string>(currentValue || ""); // Ensure default to empty string
    const [ isSubmitting, setIsSubmitting ] = useState(false);
    const [ alert, setAlert ] = useState<{ title?: string, message: string; type: MessageType } | null>(null);

    const handleInputChange = (newValue: string) => {
        setValue(newValue); // Update state with the new value
    };

    const handleSubmit = async () => {
        if (value.trim() === "") {
            setAlert({
                message: Messages.ERROR_ALL_FIELDS_REQUIRED,
                type: MESSAGE_TYPES.ERROR,
            });
            return;
        }

        setIsSubmitting(true);
        try {
            const response = await onSubmit(value.trim()); // Capture the response data

            if (response.success) {
                setAlert({
                    message: `${title} updated successfully.`,
                    type: MESSAGE_TYPES.SUCCESS,
                });
                // Close the modal after 3 seconds
                setTimeout(() => onClose(), 1000);
            }
        } catch (error: any) {
            console.error("Error updating material:", error);
            setAlert({
                title: `${Messages.ERROR_UPDATE_MATERIAL} ${title.toLowerCase()}`,
                message: error.message || `Error updating ${title.toLowerCase()}.`,
                type: MESSAGE_TYPES.ERROR,
            });
        } finally {
            setIsSubmitting(false);
        }
    };


    return (
        <Modal onClose={onClose}>
            <h2 className="text-lg font-semibold mb-4">{title}</h2>

            {/* Alert Banner */}
            {alert && (
                <AlertBanner
                    message={alert.message}
                    message_type={alert.type}
                    title={alert.title}
                    onClose={() => setAlert(null)}
                />
            )}

            <InputField
                type="text"
                value={value} // Controlled value from state
                onChange={(newValue) => handleInputChange(newValue)} // Update state on input change
                placeholder={`Enter new ${title.toLowerCase()}`}
                required
            />

            <div className="flex justify-end mt-4 gap-2">
                {/* Cancel Button */}
                <button onClick={onClose} className="bg-gray-200 hover:bg-gray-300 text-gray-700">
                    Cancel
                </button>

                {/* Submit Button */}
                <SubmitButton
                    isLoading={isSubmitting}
                    disabled={value.trim() === "" || isSubmitting || value.trim() === currentValue} // Disable based on conditions
                    onClick={handleSubmit}
                >
                    Submit
                </SubmitButton>
            </div>
        </Modal>
    );
};

export default EditMaterialModal;
